<!--
  NexusStatesGrid -- the ONE nexus-states picker.

  Region-grouped, multi-column checkbox grid of the 50 states + DC (Jake's
  2026-06-22 design, originally inline in DistributorInfoForm.vue), with a
  "select all in region" checkbox per region. Extracted so every nexus
  editor presents the column the same way: Compliance (TaxNexusEditor.vue),
  the SuperAdmin add form (DistributorInfoForm.vue) and the SuperAdmin
  edit page (DistributorEdit.vue).

  v-model is a flat array of canonical FULL state names (the names in
  @/data/usStates, which mirror the backend sales-tax comparison list).
  The grid never mutates the array it is given; every change emits a new
  one. Toggling a single state preserves existing order and appends;
  toggling a region rebuilds in canonical jurisdiction order.

  Test ids: the fieldset root is `rootTestid`; each state checkbox is
  `<itemTestidPrefix><slug>` (default `nexus-row-`, matching
  jurisdictionTestid); region select-all is `nexus-region-<slug>`; each
  region block is `nexus-section-<slug>` and its column grid
  `nexus-grid-<slug>`.
-->
<template>
  <fieldset
    class="nexus-grid"
    :data-testid="rootTestid"
    :disabled="disabled"
  >
    <legend v-if="legend" class="nexus-grid__legend">{{ legend }}</legend>
    <div
      v-for="region in nexusRegions"
      :key="region"
      class="nexus-grid__region"
      :data-testid="'nexus-section-' + slugOf(region)"
    >
      <div class="nexus-grid__region-head">
        <label class="nexus-grid__region-label">
          <input
            type="checkbox"
            class="nexus-grid__region-checkbox"
            :data-testid="regionTestidFor(region)"
            :checked="isRegionAllChecked(region)"
            :aria-label="'Select all in ' + region"
            @change="onRegionToggle(region, $event.target.checked)"
          />
          <span class="nexus-grid__region-name">{{ region }}</span>
        </label>
      </div>
      <div
        class="nexus-grid__columns"
        :data-testid="'nexus-grid-' + slugOf(region)"
      >
        <label
          v-for="j in jurisdictionsByRegion[region]"
          :key="j.code"
          class="nexus-grid__item"
        >
          <input
            type="checkbox"
            class="nexus-grid__checkbox"
            :data-testid="itemTestidPrefix + slugOf(j.name)"
            :checked="selectedSet.has(j.name)"
            :aria-label="j.name"
            @change="onJurisdictionToggle(j.name, $event.target.checked)"
          />
          <span class="nexus-grid__item-name">{{ j.name }}</span>
        </label>
      </div>
    </div>
  </fieldset>
</template>

<script>
import {
  US_JURISDICTIONS,
  US_REGIONS,
  jurisdictionSlug,
  regionTestid,
} from "@/data/usStates";

export default {
  name: "NexusStatesGrid",

  props: {
    modelValue: { type: Array, default: () => [] },
    disabled: { type: Boolean, default: false },
    legend: { type: String, default: "Nexus States" },
    rootTestid: { type: String, default: "nexus-states-grid" },
    itemTestidPrefix: { type: String, default: "nexus-row-" },
  },

  emits: ["update:modelValue"],

  computed: {
    nexusRegions() {
      return US_REGIONS;
    },
    jurisdictionsByRegion() {
      const groups = {};
      for (const region of US_REGIONS) {
        groups[region] = US_JURISDICTIONS.filter((j) => j.region === region);
      }
      return groups;
    },
    selectedSet() {
      return new Set(Array.isArray(this.modelValue) ? this.modelValue : []);
    },
  },

  methods: {
    slugOf(name) {
      return jurisdictionSlug(name);
    },
    regionTestidFor(region) {
      return regionTestid(region);
    },
    isRegionAllChecked(region) {
      const members = this.jurisdictionsByRegion[region] || [];
      if (members.length === 0) {
        return false;
      }
      return members.every((j) => this.selectedSet.has(j.name));
    },
    onJurisdictionToggle(name, checked) {
      const current = Array.isArray(this.modelValue)
        ? this.modelValue.slice()
        : [];
      if (checked === true) {
        if (!current.includes(name)) {
          current.push(name);
        }
      } else {
        const idx = current.indexOf(name);
        if (idx !== -1) {
          current.splice(idx, 1);
        }
      }
      this.$emit("update:modelValue", current);
    },
    onRegionToggle(region, checked) {
      const members = this.jurisdictionsByRegion[region] || [];
      const set = new Set(Array.isArray(this.modelValue) ? this.modelValue : []);
      for (const j of members) {
        if (checked === true) {
          set.add(j.name);
        } else {
          set.delete(j.name);
        }
      }
      const next = US_JURISDICTIONS.map((j) => j.name).filter((n) =>
        set.has(n)
      );
      this.$emit("update:modelValue", next);
    },
  },
};
</script>

<style scoped>
.nexus-grid {
  border: 1px solid var(--tf-border, rgba(0, 0, 0, 0.12));
  border-radius: var(--tf-radius-md, 12px);
  padding: var(--tf-sp-4, 16px);
  margin-top: var(--tf-sp-2, 8px);
}
.nexus-grid:disabled {
  opacity: 0.6;
}
.nexus-grid__legend {
  font-size: var(--tf-size-body, 1rem);
  font-weight: 600;
  color: var(--tf-fg-1, rgba(0, 0, 0, 0.87));
  padding: 0 var(--tf-sp-2, 8px);
}
.nexus-grid__region {
  margin-bottom: var(--tf-sp-4, 16px);
}
.nexus-grid__region:last-child {
  margin-bottom: 0;
}
.nexus-grid__region-head {
  margin-bottom: var(--tf-sp-2, 8px);
  padding-bottom: var(--tf-sp-1, 4px);
  border-bottom: 1px solid var(--tf-border, rgba(0, 0, 0, 0.08));
}
.nexus-grid__region-label {
  display: inline-flex;
  align-items: center;
  gap: var(--tf-sp-2, 8px);
  cursor: pointer;
}
.nexus-grid__region-name {
  font-weight: 600;
  color: var(--tf-fg-1, rgba(0, 0, 0, 0.87));
}
/* auto-fill + min track width: 3-4 columns on a wide card, fewer on narrow. */
.nexus-grid__columns {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(180px, 1fr));
  gap: var(--tf-sp-1, 4px) var(--tf-sp-4, 16px);
}
.nexus-grid__item {
  display: flex;
  align-items: center;
  gap: var(--tf-sp-2, 8px);
  padding: var(--tf-sp-1, 4px) 0;
  cursor: pointer;
}
.nexus-grid__item-name {
  color: var(--tf-fg-2, rgba(0, 0, 0, 0.7));
  font-size: var(--tf-size-body, 1rem);
  line-height: 1.3;
}
.nexus-grid__checkbox,
.nexus-grid__region-checkbox {
  cursor: pointer;
  flex: 0 0 auto;
}
</style>
